"use client";

import { AnimatePresence, motion } from "motion/react";
import type { ReactNode } from "react";
import React, { useEffect, useState } from "react";

import { cn } from "../../utils";
import { Icon } from "../Icon";

export interface CollapseProps {
  children?: ReactNode;
  isOpen?: boolean;
  onIsOpenChange?: (isOpen: boolean) => void;
  triggerElement?: ReactNode;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  iconClassName?: string;
}

export const Collapse: React.FC<CollapseProps> = ({
  children,
  triggerElement,
  className,
  triggerClassName,
  contentClassName,
  isOpen = false,
  onIsOpenChange,
  iconClassName,
}) => {
  const [collapseIsOpen, setCollapseIsOpen] = useState(isOpen);

  useEffect(() => {
    setCollapseIsOpen(isOpen);
  }, [isOpen]);

  const handleToggle = () => {
    const newState = !collapseIsOpen;
    setCollapseIsOpen(newState);

    setTimeout(() => {
      onIsOpenChange?.(newState);
    }, 0);
  };

  return (
    <div className={className}>
      <button className={triggerClassName} onClick={handleToggle}>
        {triggerElement}
        <Icon
          size="xs"
          name="caret-down"
          className={cn(
            "ui:transition-transform",
            {
              "ui:rotate-180": collapseIsOpen,
            },
            iconClassName,
          )}
        />
      </button>
      <AnimatePresence>
        {Boolean(collapseIsOpen) && (
          <motion.div
            exit={{ height: 0 }}
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className={cn("overflow-hidden", contentClassName)}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
